import { HttpClient, HttpEventType, HttpHeaders, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, of } from 'rxjs';
import { catchError, last, map, tap } from 'rxjs/operators';
import { PageService } from '../../../shared/page.service';
import { Carousel, Conversation, InMessageFile, Media, Message, MessageTypeEnum, TextButtonTypeEnum } from '../shared';

import { DatePipe } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';
import { environment } from '../../../../environments/environment';
import { JWTService } from '../../../shared/jwt.service';

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
};

@Injectable()
export class MessageService {
  private SERVER_URL = environment.apiURLPrefix;

  private IMAGE_WIDTH_VW = 60; // TODO this UI constant doesn't belong here,
  // we shouldn't hardcode it in the CSS but instead inject a <style> bloc at bootstrap

  constructor(
    private datePipe: DatePipe,
    private http: HttpClient,
    private domSanitizer: DomSanitizer,
    private pageService: PageService,
    private jwtService: JWTService
  ) {}

  /*
   * GET the 10 latest messages from the conversation
   */
  getMostRecentMessages(): Observable<Conversation> {
    return this.getMessages(undefined, undefined);
  }

  /*
   * GET the 10 messages immedialtely older than the given id
   */
  getOlderMessages(maxMessageId: number): Observable<Message[]> {
    return this.getMessages(undefined, maxMessageId).pipe(map(conversation => conversation?.messages || []));
  }

  /*
   * GET all messages newer than the message having the given id
   */
  getNewerMessages(minMessageId: number): Observable<Message[]> {
    return this.getMessages(minMessageId, undefined).pipe(map(conversation => conversation?.messages || []));
  }

  /*
   * Retrieves existing messages in the user's conversation with the current page.
   * If maxMessageId is defined, retrieves the 10 messages just older than the given message id;
   * else if minMessageId is defined, retrieves all messages newer than the given message if;
   * otherwise retrieves the latest 10 messages
   */
  private getMessages(minMessageId: number, maxMessageId: number): Observable<Conversation> {
    let endpoint = this.SERVER_URL + this.pageService.getPageId() + '/conversation';
    if (maxMessageId) {
      endpoint += `?max_message_id=${maxMessageId}`;
    } else if (minMessageId) {
      endpoint += `?min_message_id=${minMessageId}`;
    }
    return this.http.get<Conversation>(endpoint, httpOptions).pipe(
      tap(({ messages }) => {
        for (const message of messages) {
          if (!message.datetime.endsWith('Z')) {
            message.datetime += '.000Z';
          }
          message.dateLabel = this.datePipe.transform(message.datetime, 'EEEE d/M');
          this.splitMessageContentByType(message);
          this.injectURLForImageOrFileMessages(message);
          this.sanitizeButtonURLs(message);
        }
      }),
      catchError(this.handleError('get messages', null))
    );
  }

  /*
   * POST a message to the user's conversation
   */
  postMessage(message: Message): Observable<Message> {
    const endpoint = this.SERVER_URL + this.pageService.getPageId() + '/conversation';

    let body = message.toBackendBody();
    body = this.addReferrerUrl(body);

    return this.http.post<Message>(endpoint, body, httpOptions).pipe(
      catchError(() => {
        // If an error occured, we recycle the input message, with an error flag
        message.postFailed = true;
        return of(message);
      })
    );
  }

  private addReferrerUrl(body) {
    const urlParams = new URLSearchParams(window.location.search);
    const referrerUrl = urlParams.get('referrerUrl');

    if (referrerUrl) {
      const referrerUrlUrl = new URL(referrerUrl);

      // Obfuscate value of params with name containing 'jwt'
      referrerUrlUrl.searchParams.forEach((value, key) => {
        if (key.includes('jwt')) {
          referrerUrlUrl.searchParams.set(key, 'xxxx');
        }
      });

      body[body.type].referrer_url = referrerUrlUrl.toString();
    }

    return body;
  }

  uploadFile(file: File, nativeProgress: Subject<number>): Observable<string> {
    const endpoint = this.SERVER_URL + this.pageService.getPageId() + '/file';

    const formData = new FormData();
    formData.set('file_name', file.name);
    formData.set('file_content', file);

    const req = new HttpRequest('POST', endpoint, formData, {
      reportProgress: true,
    });
    // The `HttpClient.request` API produces a raw event stream
    // which includes start (sent), progress, and response events.
    return this.http.request(req).pipe(
      map(event => {
        let percentDone: number;
        switch (event.type) {
          case HttpEventType.Sent:
            nativeProgress.next(0);
            return `Uploading file of size ${file.size}.`;
          case HttpEventType.UploadProgress:
            // Compute and show the % done:
            percentDone = Math.round((100 * event.loaded) / event.total);
            nativeProgress.next(percentDone);
            return `${percentDone}% uploaded.`;
          case HttpEventType.Response:
            nativeProgress.next(100);
            return event.body['uuid'];
          default:
            return `ignored event type ${event.type}`;
        }
      }),
      last() // return last (completed) message to caller
    );
  }

  private sanitizeButtonURLs(message: Message) {
    if (message?.text?.buttons) {
      for (const textButton of message.text.buttons) {
        if (textButton.type === TextButtonTypeEnum.URL_BUTTON) {
          const button = textButton[TextButtonTypeEnum.URL_BUTTON];
          button.sanitizedUrl = this.domSanitizer.bypassSecurityTrustUrl(button.web_url);
        }
      }
    }
  }

  private splitMessageContentByType(message: Message) {
    if (message?.text?.content) {
      const emojiPattern = /(\p{Extended_Pictographic})/gu;
      const hasEmojis = emojiPattern.test(message.text.content);
      if (hasEmojis) {
        message.text.parts = message.text.content
          .split(emojiPattern)
          // Filter empty index due to emojis.
          .filter(i => i)
          .map(text => {
            if (emojiPattern.test(text)) {
              // The browser reader should not read the emojis.
              return { ariaHidden: true, text };
            }
            return { text };
          });
      }
    }
  }

  private injectURLForImageOrFileMessages(message: Message) {
    if (!message) {
      return;
    }
    if (message.type === MessageTypeEnum.IMAGE) {
      this.injectImageURL(message.image);
    } else if (message.type === MessageTypeEnum.FILE) {
      this.injectFileURL(message.file);
    } else if (message.type === MessageTypeEnum.CAROUSEL) {
      this.injectCarouselImageURL(message.carousel);
    }
  }

  private injectCarouselImageURL(carousel: Carousel) {
    carousel.slides.forEach(slide => {
      slide.pictureURL =
        `${this.SERVER_URL}${this.pageService.getPageId()}` +
        `/media/${slide.media_uuid}?${this.jwtService.JWT_HEADER_AND_PARAM_NAME}=${this.jwtService.getJwt()}`;
    });
  }

  public injectImageURL(image: Media) {
    image.url =
      `${this.SERVER_URL}${this.pageService.getPageId()}` +
      `/media/${image.media_uuid}?${this.jwtService.JWT_HEADER_AND_PARAM_NAME}=${this.jwtService.getJwt()}`;
  }

  public injectFileURL(inMessageFile: InMessageFile) {
    // If file is an image then use /media endpoint that returns a reduced version of the image so that
    // conversation loads light images.
    // Else use /file endpoint that returns original files.
    let endpoint: string;
    if (inMessageFile.mime_type?.startsWith('image') && inMessageFile.mime_type !== 'image/gif') {
      endpoint = '/media';
    } else {
      endpoint = '/file';
    }
    inMessageFile.url =
      `${this.SERVER_URL}${this.pageService.getPageId()}` +
      endpoint +
      `/${inMessageFile.uuid}?${this.jwtService.JWT_HEADER_AND_PARAM_NAME}=${this.jwtService.getJwt()}`;
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      console.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result);
    };
  }

  public downloadTranscript(): Observable<{ uuid: string }> {
    const url = this.SERVER_URL + this.pageService.getPageId() + '/transcript';
    return this.http.post<{ uuid: string }>(url, {}).pipe(catchError(this.handleError('download transcript', null)));
  }
}
